<template>
  <div class="relative flex gap-1.5 flex-col lg:flex-row justify-center">
    <div v-if="isBalanceView" class="flex flex-col gap-1.5 w-72 shrink-0">
      <PanelWindow title="Datoer for saldo nulstilling">
        <div class="space-y-3.5">
          <div v-for="eventType in eventTypesWithBalance" :key="eventType">
            <p class="text-sm font-medium text-neutral-900">{{ eventType }}</p>
            <p class="font-semibold text-md text-neutral-800">
              {{ eventsStore.getDeadline(eventType) || 'Ikke angivet' }}
            </p>
          </div>
        </div>
      </PanelWindow>
    </div>

    <div class="relative w-full overflow-x-auto bg-white border border-solid rounded-lg shadow-lg border-neutral-300 lg:self-start">
      <!-- Controls -->
      <div class="flex items-center justify-between m-1.5 px-1.5 w-full mx-auto">
        <div class="flex items-center space-x-1.5">
          <!-- Date Range Button -->
          <button v-if="!isBalanceView" @click="openModal"
            class="h-[40px] flex items-center px-4 py-2 rounded-lg text-neutral-800 bg-neutral-100 hover:bg-neutral-200 justify-center">
            <span class="text-sm font-medium text-black">
              {{ formatDateRange(startDate, endDate) }}
            </span>
          </button>
        </div>

        <!-- View toggle buttons -->
        <div class="flex items-center space-x-1.5">
          <!-- Count View Button -->
          <button @click="toggleView('count')"
            class="w-[40px] h-[40px] flex items-center sm:space-x-2 p-2 sm:pr-3 sm:w-28 rounded-lg justify-center text-sm"
            :disabled="!isBalanceView" :class="{
              'text-white bg-neutral-800': !isBalanceView,
              'text-neutral-800 bg-neutral-100 hover:bg-neutral-200': isBalanceView,
            }">
            <SquareChartGantt :size="18" class="-mt-0.5" :stroke-width="1.75" />
            <span class="hidden text-sm font-medium sm:inline">Optælling</span>
          </button>
          <!-- Balance View Button -->
          <button @click="toggleView('balance')"
            class="w-[40px] h-[40px] flex items-center sm:space-x-2 p-2 sm:pr-3 sm:w-28 rounded-lg justify-center text-sm"
            :disabled="isBalanceView" :class="{
              'text-white bg-neutral-800': isBalanceView,
              'text-neutral-800 bg-neutral-100 hover:bg-neutral-200': !isBalanceView,
            }">
            <Wallet :size="18" class="-mt-0.5" :stroke-width="1.75" />
            <span class="hidden text-sm font-medium sm:inline">Saldo</span>
          </button>
        </div>
      </div>

      <div
        class="max-h-[calc(100vh-11rem)] overflow-y-auto relative overflow-x-auto bg-white border-t border-solid rounded-b-lg border-neutral-200">
        <component :is="currentView" :users="users" :start-date="startDate" :end-date="endDate" @show-user-info="showUserInfo" @hide-user-info="hideUserInfo" />
      </div>
    </div>

    <!-- User Info Popover -->
    <UserInfoPopover v-if="userInfo" :user="activeUser" :initial-x="infoX" :initial-y="infoY" @close="hideUserInfo"
      :is-visible="userInfo" />

    <!-- Date Range Picker Modal -->
    <ReusableModal :isOpen="showModal" :isConfirm="true" @close="closeModal" @submit="applyDateRange" title="Vælg datointerval">
      <DateRangeSelection v-model:start-date="tempStartDate" v-model:end-date="tempEndDate" />
    </ReusableModal>
  </div>
</template>

<script setup>
import { ref, defineProps, inject, computed, onMounted, markRaw } from 'vue';
import UserBalanceView from "@/components/ui/AdminDashboard/Views/UserBalanceView.vue";
import UserCountView from "@/components/ui/AdminDashboard/Views/UserCountView.vue";
import UserInfoPopover from "@/components/ui/AdminDashboard/Popovers/UserInfoPopover.vue";
import PanelWindow from "@/components/ui/PanelWindow.vue";
import ReusableModal from '@/components/ui/ReusableModal.vue';
import DateRangeSelection from '@/components/ui/AdminDashboard/DateRangeSelection.vue';
import { Wallet, SquareChartGantt } from 'lucide-vue-next';

const eventsStore = inject("eventsStore");

defineProps({
  users: Array,
});

const userInfo = ref(false);
const activeUser = ref({});
const infoX = ref(0);
const infoY = ref(0);
const isBalanceView = ref(false);
const showModal = ref(false);

// Date range state
const startDate = ref(new Date(new Date().getFullYear(), 0, 1));
const endDate = ref(new Date(new Date().getFullYear(), 11, 31));
const tempStartDate = ref(new Date(startDate.value));
const tempEndDate = ref(new Date(endDate.value));

// Computed properties
const currentView = computed(() => isBalanceView.value ? markRaw(UserBalanceView) : markRaw(UserCountView));
const eventTypesWithBalance = computed(() => eventsStore.getEventTypesWithBalance);

function showUserInfo(user, x, y) {
  activeUser.value = user;
  userInfo.value = true;
  infoX.value = x;
  infoY.value = y;
}

function hideUserInfo() {
  userInfo.value = false;
}

function toggleView(view) {
  isBalanceView.value = view === 'balance';
}

function openModal() {
  tempStartDate.value = new Date(startDate.value);
  tempEndDate.value = new Date(endDate.value);
  showModal.value = true;
}

function closeModal() {
  showModal.value = false;
}

function applyDateRange() {
  startDate.value = new Date(tempStartDate.value);
  endDate.value = new Date(tempEndDate.value);
  closeModal();
}

function formatDateRange(start, end) {
  const formatDate = (date) => {
    return new Intl.DateTimeFormat('da-DK', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      dateStyle: undefined // Remove the dateStyle option
    }).format(date).replace(/\./g, '/'); // Replace dots with slashes
  };
  return `${formatDate(start)} - ${formatDate(end)}`;
}

// Lifecycle hook
onMounted(async () => {
  await eventsStore.fetchBalanceUpdateSchedules();
});
</script>